import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import type { FileValidationFailure, FileValidationResult } from "@/shared/utils/fileValidation";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../primitives/form";
import { ColocaFileUpload, type ColocaFileUploadLabels } from "./ColocaFileUpload";

interface ColocaFileUploadFieldProps {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the dropzone. */
  label?: ReactNode;
  /** Helper text rendered below the dropzone. Do not repeat the label. */
  description?: ReactNode;
  /** Translated dropzone texts — see `ColocaFileUpload`. */
  labels: ColocaFileUploadLabels;
  /** Accepted MIME types for the native file input. */
  accept?: string;
  /** Maximum file size in megabytes. */
  maxSizeMB?: number;
  /** Replaces the default image validator — see `ColocaFileUpload`. */
  validate?: (file: File) => FileValidationResult;
  /** Receives the failure of a refused file; pair with `form.setError(name, …)` to show it. */
  onReject?: (result: FileValidationFailure, file: File) => void;
  /** Replaces the image preview of the selected state — see `ColocaFileUpload`. */
  renderPreview?: (file: File) => ReactNode;
  disabled?: boolean;
}

/**
 * Controlled single-file dropzone wired to the nearest React Hook Form context.
 *
 * Use inside a `<ColocaForm>` for any field whose Zod schema expects a `File` (e.g. `z.instanceof(File)`).
 * Validation errors are displayed automatically below the dropzone via `<FormMessage>`.
 */
export function ColocaFileUploadField({
  name,
  label,
  description,
  labels,
  accept,
  maxSizeMB,
  validate,
  onReject,
  renderPreview,
  disabled,
}: ColocaFileUploadFieldProps) {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <ColocaFileUpload
              value={field.value instanceof File ? field.value : null}
              onChange={(file) => {
                field.onChange(file);
                field.onBlur();
              }}
              labels={labels}
              accept={accept}
              maxSizeMB={maxSizeMB}
              validate={validate}
              onReject={onReject}
              renderPreview={renderPreview}
              invalid={fieldState.invalid}
              disabled={disabled}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
